import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Table, TableBody, TableContainer, TableHead, TableRow, Paper, Container, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { Download } from "@mui/icons-material";

import { useState, useEffect } from "react";

import Config from "../config.json";
import { dateToStringDate } from "../Helpers";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const INFO_FINANCIAL = "The Evaluation of Financial Proposals is still in progress";
const INFO_NEGOTIATION = "Contract Negotiation is still in progress";

const procurementTimeline = [
  { no: 1, activity: "Announcement", start: "February 5, 2026 16:00", end: "February 20, 2026 11:59" },
  { no: 2, activity: "Submission of EOI", start: "February 5, 2026 16:01", end: "February 20, 2026 11:59" },
  { no: 3, activity: "Shortlist Appointment", start: "February 20, 2026 12:00", end: "May 8, 2026 23:59" },
  { no: 4, activity: "Shortlist Announcement and Issuance of RFP", start: "May 11, 2026 00:00", end: "June 29, 2026 23:59" },
  { no: 5, activity: "Clarification Request Period", start: "May 13, 2026 00:00", end: "June 18, 2026 09:00" },
  { no: 6, activity: "Submission of Technical and Financial Proposals", start: "May 11, 2026 00:00", end: "June 29, 2026 11:59" },
  { no: 7, activity: "Opening of Tech Proposal", start: "June 29, 2026 13:00", end: "June 29, 2026 23:59" },
  { no: 8, activity: "Evaluation of Technical Proposals", start: "June 29, 2026 13:00", end: "September 9, 2026 23:59" },
  { no: 9, activity: "Technical Rank Announcement", start: "September 10, 2026 00:00", end: "September 10, 2026 23:59" },
  { no: 10, activity: "Opening of Financial Proposals", start: "September 25, 2026 10:00", end: "September 25, 2026 13:00" },
  { no: 11, activity: "Evaluation of Financial Proposals", start: "September 25, 2026 13:01", end: "October 7, 2026 15:59", information: INFO_FINANCIAL },
  { no: 12, activity: "Combined Evaluation", start: "October 7, 2026 00:00", end: "October 7, 2026 15:59", information: INFO_FINANCIAL },
  { no: 13, activity: "Final Ranking", start: "October 7, 2026 16:00", end: "October 7, 2026 23:59", information: INFO_FINANCIAL },
  { no: 14, activity: "Contract Negotiation", start: "October 8, 2026 00:00", end: "October 19, 2026 23:59", information: INFO_NEGOTIATION },
  { no: 15, activity: "Notification of Intention of Award", start: "October 23, 2026 00:00", end: "October 23, 2026 23:59", information: INFO_NEGOTIATION },
  { no: 16, activity: "Standstill Period", start: "October 26, 2026 00:00", end: "November 6, 2026 23:59", information: INFO_NEGOTIATION },
  { no: 17, activity: "Notification of Award", start: "November 9, 2026 00:00", end: "November 9, 2026 23:59", information: INFO_NEGOTIATION },
  { no: 18, activity: "Contract Signing", start: "November 10, 2026 00:00", end: "November 18, 2026 23:59", information: INFO_NEGOTIATION },
];

function GeoClimate() {
  const url_list = Config.api_domain + "/geoclimate/active/";
  const [list, setList] = useState([]);
  const [openAnnouncementPopup, setOpenAnnouncementPopup] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(url_list, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("Server error");

        const data = await res.json();
        const sorted = (data.data || []).sort(
          (a, b) => new Date(b.time_uploaded) - new Date(a.time_uploaded)
        );
        setList(sorted);
      } catch (error) {
        console.error("Fetch error:", error.message);
      }
    };

    fetchData();
  }, [url_list]);

  function getRows() {
    if (list && list.length > 0) {
      return list.map((row, index) => {
        let dateISO = new Date(row.time_uploaded);
        let formatted_datetime = dateToStringDate(dateISO);
        return (
          <StyledTableRow key={row.public_id}>
            <StyledTableCell>{index + 1}</StyledTableCell>
            <StyledTableCell>{row.title}</StyledTableCell>
            <StyledTableCell>{formatted_datetime}</StyledTableCell>
            <StyledTableCell>
              <IconButton
                aria-label="download"
                component="a"
                href={Config.api_domain + "/geoclimate/download/" + row.public_id}
                download
              >
                <Download />
              </IconButton>
            </StyledTableCell>
          </StyledTableRow>
        );
      });
    } else {
      return (
        <StyledTableRow key="no-data">
          <StyledTableCell colSpan={4} align="center">
            No Data Found
          </StyledTableCell>
        </StyledTableRow>
      );
    }
  }

  return (
    <div style={{ padding: "1em 2em 3em 2em" }}>
      <Container>

        {/* ✅ Announcement Popup */}
        <Dialog
          open={openAnnouncementPopup}
          onClose={() => setOpenAnnouncementPopup(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Announcement – Opening of Financial Proposals
          </DialogTitle>

          <DialogContent dividers>
            <DialogContentText
              component="div"
              sx={{
                whiteSpace: "pre-line",
                fontSize: "0.9rem",
                color: "#333",
                lineHeight: 1.8,
              }}
            >
              <>
                ILASP PHLN is inviting you to a scheduled Zoom meeting.
                <br /><br />
                <strong>Topic:</strong> Opening of Financial Proposals for Geospatial Data Utilization Platform For Climate Action
                <br />
                <strong>Time:</strong> September 25, 2026 10:00 AM Jakarta
                <br /><br />
                <strong>Meeting ID:</strong> 912 6009 6923
                <br />
                <strong>Passcode:</strong> Hp4@a2Zy
                <br /><br />
                <strong>Join Zoom Meeting:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/j/91260096923?pwd=Ab9va5A7PvFJ9tHuoSk5uuB7yB3CAJ.1"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1b5e20", textDecoration: "underline", wordBreak: "break-all" }}
                >
                  https://big-go-id.zoom.us/j/91260096923?pwd=Ab9va5A7PvFJ9tHuoSk5uuB7yB3CAJ.1
                </a>
                <br /><br />
                <strong>Meeting Chat Link:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/launch/jc/91260096923"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1b5e20", textDecoration: "underline", wordBreak: "break-all" }}
                >
                  https://big-go-id.zoom.us/launch/jc/91260096923
                </a>
                <br /><br />
                <strong>Join by SIP:</strong> 91260096923@zoomcrc.com
                <br /><br />
                <strong>Join Instructions:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/meetings/91260096923/invitations?signature=jIaQ3XoWvwPnI3tr7DdEytjoU5qtli0ey94ZUwnkhvM"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1b5e20", textDecoration: "underline", wordBreak: "break-all" }}
                >
                  https://big-go-id.zoom.us/meetings/91260096923/invitations?signature=jIaQ3XoWvwPnI3tr7DdEytjoU5qtli0ey94ZUwnkhvM
                </a>
              </>
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenAnnouncementPopup(false)}
              variant="contained"
              sx={{ backgroundColor: "#1b5e20", "&:hover": { backgroundColor: "#145214" } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* === CONTENT === */}
        <h2>Geospatial Data Utilization Platform For Climate Action</h2>

        <h3>Project Description</h3>
        <p style={{ textAlign: "justify" }}>
          As part of the implementation of the Integrated Land Administration and Spatial Planning (ILASP) Project, the Geospatial Information Agency (BIG) is seeking a consulting firm (or consortium) to provide data and platform development consultation services. The objective is to develop platform allowing geospatial data and information utilization for climate action and spatial planning. This assignment, scheduled for the period 2026–2029, will involve developing platform and specification, integrating geospatial data, as well as analyse thematic data and modelling. The selected consultant will act on behalf of BIG, work in close coordination with the Directorate of Atlas and Geospatial Information Utilization, and submit reports and documentation that will serve as the basis for project-related payment processes.
        </p>

        <h3>Announcement</h3>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 500 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>No</StyledTableCell>
                <StyledTableCell>Announcement</StyledTableCell>
                <StyledTableCell>Publication Date</StyledTableCell>
                <StyledTableCell>Download</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getRows()}
            </TableBody>
          </Table>
        </TableContainer>
        <h3>Procurement Timeline</h3>
        <TableContainer component={Paper} sx={{ mb: 6 }}>
          <Table sx={{ minWidth: 500 }} aria-label="timeline table">
            <TableHead>
              <TableRow>
                <StyledTableCell>No.</StyledTableCell>
                <StyledTableCell>Activity</StyledTableCell>
                <StyledTableCell>Start Date</StyledTableCell>
                <StyledTableCell>End Date</StyledTableCell>
                <StyledTableCell>Information</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {procurementTimeline.map((row) => (
                <StyledTableRow key={row.no}>
                  <StyledTableCell>{row.no}</StyledTableCell>
                  <StyledTableCell>{row.activity}</StyledTableCell>
                  <StyledTableCell>{row.start} (GMT+7)</StyledTableCell>
                  <StyledTableCell>{row.end} (GMT+7)</StyledTableCell>
                  <StyledTableCell>{row.information || ""}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          All schedules are based on Jakarta time (GMT +7)
        </Typography>
        <p style={{ textAlign: "justify" }}>
          The package will be processed through SPSE ICB at the link:{" "}
          <a
            href="https://spseicb.lkpp.go.id/spseicb/lelang/view/2630999"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://spseicb.lkpp.go.id/spseicb/lelang/view/2630999
          </a>

        </p>
      </Container>
    </div>
  );
}

export default GeoClimate;
